export type ImpressionSection = 'interpretation' | 'riskFactors';

const SECTION_ATTRIBUTE = 'data-csi-impression-section';

const LEGACY_CONFIG: {
  [key in ImpressionSection]: { containerClass: string; headingKeyword: string };
} = {
  interpretation: {
    containerClass: 'interpretation-data-container',
    headingKeyword: 'interpretation'
  },
  riskFactors: {
    containerClass: 'risk-factors-data-container',
    headingKeyword: 'risk factors'
  }
};

export function upsertImpressionsSection(
  currentImpressions: string,
  section: ImpressionSection,
  sectionHtml: string
): string {
  const root = parseImpressionsRoot(currentImpressions);
  removeLegacySection(root, section);

  const existingSectionNode = findTaggedSectionNode(root, section);

  if (!hasMeaningfulHtml(sectionHtml)) {
    if (existingSectionNode) existingSectionNode.remove();
    return root.innerHTML;
  }

  if (existingSectionNode) {
    existingSectionNode.innerHTML = sectionHtml;
    return root.innerHTML;
  }

  const sectionNode = root.ownerDocument.createElement('div');
  sectionNode.setAttribute(SECTION_ATTRIBUTE, section);
  sectionNode.innerHTML = sectionHtml;
  root.appendChild(sectionNode);

  return root.innerHTML;
}

export function readImpressionsSection(
  currentImpressions: string,
  section: ImpressionSection
): string {
  const root = parseImpressionsRoot(currentImpressions);

  const existingSectionNode = findTaggedSectionNode(root, section);
  if (existingSectionNode && hasMeaningfulHtml(existingSectionNode.innerHTML)) {
    return existingSectionNode.innerHTML;
  }

  return readLegacySection(root, section);
}

export function hasImpressionsSectionContent(
  currentImpressions: string,
  section: ImpressionSection
): boolean {
  return hasMeaningfulHtml(readImpressionsSection(currentImpressions, section));
}

function parseImpressionsRoot(currentImpressions: string): HTMLElement {
  const root = document.createElement('div');
  root.innerHTML = (currentImpressions || '').toString();
  return root;
}

function findTaggedSectionNode(
  root: HTMLElement,
  section: ImpressionSection
): HTMLElement | null {
  return root.querySelector(`div[${SECTION_ATTRIBUTE}="${section}"]`) as HTMLElement;
}

function readLegacySection(root: HTMLElement, section: ImpressionSection): string {
  const { containerClass, headingKeyword } = LEGACY_CONFIG[section];
  const containers = Array.from(root.querySelectorAll(`.${containerClass}`)) as HTMLElement[];

  for (const container of containers) {
    const heading = getLegacyHeading(container, headingKeyword);
    if (heading && hasMeaningfulHtml(container.innerHTML)) {
      return container.innerHTML;
    }
  }

  return '';
}

function removeLegacySection(root: HTMLElement, section: ImpressionSection): void {
  const { containerClass, headingKeyword } = LEGACY_CONFIG[section];
  const containers = Array.from(root.querySelectorAll(`.${containerClass}`)) as HTMLElement[];

  containers.forEach(container => {
    const heading = getLegacyHeading(container, headingKeyword);
    if (heading) heading.remove();
    container.remove();
  });
}

function getLegacyHeading(container: HTMLElement, headingKeyword: string): HTMLElement | null {
  const headingCandidate = container.previousElementSibling as HTMLElement;
  if (
    headingCandidate &&
    headingCandidate.tagName === 'H4' &&
    (headingCandidate.textContent || '').toLowerCase().includes(headingKeyword)
  ) {
    return headingCandidate;
  }

  return null;
}

function hasMeaningfulHtml(html: string): boolean {
  if (!html) return false;
  const normalized = html
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, '')
    .trim();
  return normalized.length > 0;
}
